/**
 * [UPSELL] DodoMarket add-on offers: PDP "Complete the gift" controller, cart drawer grids (drawer mode `new`)
 * + staff debug overlay.
 *
 * ES6, isolated (like ddm-availability.js / smart-cart.js), not minified.
 * Loaded with `defer` from snippets/cart-drawer.liquid while settings.upsell_pdp_enabled is on or
 * settings.upsell_drawer_mode is `new`.
 * Markup + config contract: snippets/upsell-pdp-block.liquid; drawer grids: snippets/upsell-drawer-grids.liquid
 * (page) and snippets/upsell-drawer-feed.liquid (with the cart).
 * Add hook: assets/theme.js, AjaxProduct.addItemFromForm calls DDMUpsell.afterMainAdd(form, line). The extras are
 * captured earlier, when that submission starts (onSubmit): theme.js itself is not touched.
 *
 * Rules that must hold:
 *  - extras are opt-in, max 3, and are added only AFTER the main add succeeded, in ONE POST /cart/add.js
 *  - what is added is the snapshot taken when the main submission STARTED (ids, properties, shown prices, titles);
 *    the block is locked from then until the extras settle; every path releases it (failure, exception, and at the
 *    deadline the UI only: a late main answer still adds the snapshot)
 *  - the main line is never lost: afterMainAdd never throws, its promise never rejects and always settles
 *    within EXTRAS_DEADLINE_MS; an uncertain write is reconciled from /cart.js, never re-posted
 *  - card price = cart price: both prices come from Liquid, the live Prime state picks one, and an extra is
 *    never posted when the cart would charge more than its card showed
 *  - a product that is in the cart is not offered (state follows the cart events, no polling)
 *  - a card's variant field never ticks; the variant chosen there is the one priced, named, guarded and added
 *  - there is no price cap: which cards show depends only on the pool order, the rules' max_items and the cart
 *  - all listeners are delegated on document, so re-rendered sections need no re-init
 */
(function (window, document) {
  'use strict';

  if (!window) return;

  // ONE deadline for the whole extras sequence (batch POST, cart reconciliation, serial retries). The main item
  // is already in the cart while it runs, and the add-to-cart button stays in its loading state: never longer
  // than this. theme.js arms its own safety timer from the same number (DDMUpsell.deadlineMs).
  const EXTRAS_DEADLINE_MS = 7000;

  /* ------------------------------------------------------------------ *
   * Pure logic (no DOM): unit-tested in tests/ddm-upsell.test.js
   * ------------------------------------------------------------------ */
  const logic = {
    EXTRAS_DEADLINE_MS,

    // What the cart will charge for the main item: the member price when the cart has Prime benefits and the
    // product carries a prime-N tag (snippets/cart-item.liquid:76-89). Same Prime state as the extras, so the
    // button total is the cart total for main + extras, never a mix of member and non-member prices.
    mainUnit(variantPrice, main, primeOn) {
      if (primeOn && main && main.primeDiscount > 0) {
        return Math.round((variantPrice * (100 - main.primeDiscount)) / 100);
      }
      return variantPrice;
    },

    // Prime benefits apply to the cart when the shopper is a member or the DodoPrime variant is in the cart
    // (snippets/check-prime-cart.liquid), or is about to be: the PDP "add DodoPrime" opt-in is ticked.
    primeOn(prime) {
      return !!(prime && (prime.member || prime.inCart || prime.optIn));
    },

    // Price the cart line will show for an extra (snippets/cart-item.liquid:76-89). Liquid computes both
    // prices; this only picks one. Card price and running total both use it.
    charged(item, primeOn) {
      return primeOn && item.primePrice != null ? item.primePrice : item.price;
    },

    // Which pool items are visible. flags[i] = { checked, disabled, inCart }.
    // A product that is in the cart is never offered. Ticked and sold-out options always stay. Then unticked
    // options in pool order, until the row holds rowMax items and each rule is within its max_items. The pool
    // is larger than the row only so that the row refills when an item leaves for the cart. No price is involved.
    visibility(items, flags, rowMax) {
      const visible = items.map(() => false);
      const perRule = {};
      let shown = 0;
      items.forEach((item, i) => {
        const flag = flags[i] || {};
        if (flag.inCart) return;
        if (flag.checked || flag.disabled) {
          visible[i] = true;
          shown += 1;
          perRule[item.rule] = (perRule[item.rule] || 0) + 1;
        }
      });
      items.forEach((item, i) => {
        if (visible[i] || (flags[i] && flags[i].inCart)) return;
        const used = perRule[item.rule] || 0;
        if (shown < rowMax && used < (item.max || rowMax)) {
          visible[i] = true;
          shown += 1;
          perRule[item.rule] = used + 1;
        }
      });
      return visible;
    },

    // Running total = main unit x quantity + ticked extras (one each), all at the price the cart will charge.
    totals(mainUnit, qty, items, flags, primeOn) {
      let count = 0;
      let extras = 0;
      items.forEach((item, i) => {
        const flag = flags[i];
        if (flag && flag.checked && !flag.disabled && !flag.inCart) {
          count += 1;
          extras += logic.charged(item, primeOn);
        }
      });
      return { count, extras, total: mainUnit * qty + extras };
    },

    // The shopper picked a variant in the card's field (snippets/upsell-pdp-block.liquid, item.variants): from now on
    // the item prices, names and adds that variant. The product (item.p) stays: dedupe and in-cart detection are by
    // product. Returns the variant, or null when the item has no field or does not offer that id.
    chooseVariant(item, id) {
      const variant = (item.variants || []).find((candidate) => String(candidate.v) === String(id));
      if (!variant) return null;
      item.v = variant.v;
      item.price = variant.price;
      item.primePrice = variant.primePrice;
      item.title = item.name + ' - ' + variant.title; // the cart line's name, like Liquid's default
      return variant;
    },

    // Which extras go into the add: every ticked one, up to rowMax.
    // `shown` = the price on the card when the shopper pressed add; addExtras refuses to charge more than that.
    selection(items, flags, rowMax, primeOn) {
      const picked = [];
      items.forEach((item, index) => {
        const flag = flags[index];
        if (flag && flag.checked && !flag.disabled && !flag.inCart && picked.length < rowMax) {
          picked.push({
            index,
            id: item.v,
            product: item.p,
            title: item.title,
            // A card with a field links the chosen variant (alert links, Staging QA D6); item.url keeps the /fr prefix
            url: item.variants && item.url ? item.url + (item.url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + item.v : item.url,
            price: item.price,
            primePrice: item.primePrice,
            shown: logic.charged(item, primeOn),
            properties: item.props,
          });
        }
      });
      return picked;
    },

    // The id of a form element. NEVER `form.id`: a product form contains <select name="id">, and a form exposes
    // its named controls as properties, so `form.id` is that select (same trap for any `form.<control name>`,
    // e.g. form.quantity). Attributes and methods are safe.
    formId(form) {
      return (form && typeof form.getAttribute === 'function' && form.getAttribute('id')) || '';
    },

    // "[token]" placeholders, the theme.strings pattern
    fill(template, values) {
      return String(template || '').replace(/\[(\w+)\]/g, (match, key) =>
        Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
      );
    },

    // What a /cart.js snapshot says: product ids in the cart, and whether the DodoPrime variant is one of them
    cartState(cart, primeVariantId) {
      const products = {};
      let hasPrime = false;
      ((cart && cart.items) || []).forEach((line) => {
        products[line.product_id] = true;
        if (primeVariantId && String(line.variant_id) === String(primeVariantId)) hasPrime = true;
      });
      return { products, hasPrime };
    },

    // Which requested extras did land in the cart? Used after a rejected or uncertain write
    // (whether a multi-item add is atomic is undocumented, so ask the cart).
    // baselineQty = { line key: quantity } from the read taken before posting: a line that was already there
    // with the same quantity is not a landed write.
    landed(requested, cartItems, parentKey, baselineQty) {
      const byVariant = {};
      (cartItems || []).forEach((line) => {
        if (!line.properties || line.properties._upsell_parent !== parentKey) return;
        const before = baselineQty && baselineQty[line.key];
        if (before && line.quantity <= before) return;
        byVariant[line.variant_id] = line;
      });
      const added = [];
      const missing = [];
      const found = [];
      requested.forEach((item) => {
        const line = byVariant[item.id];
        if (line) {
          added.push(line);
          found.push({ item, line });
        } else {
          missing.push(item);
        }
      });
      return { added, missing, found };
    },

    // Add the extras. Resolves, exactly once and at the latest after the deadline, with
    //   { added: [cart lines], failed: [{ id, reason: 'sold_out' | 'price' | 'lost', message }],
    //     skipped: [ids already in the cart], cart: pre-post /cart.js snapshot or null,
    //     mainLost, mainUnknown, uncertain, timedOut }.
    // Never rejects. io = { post(items, signal) -> { ok, status, body }, getCart(signal) -> cart }.
    // NOTHING runs after it resolved: no request, no timer, no later correction. The cart drawer is rebuilt
    // from the real cart when it opens and is the source of truth.
    //
    // The module writes ONLY what the shopper asked for, once: one POST with the ticked extras. After a definite
    // rejection of that batch (422) the same extras are retried one by one. Every other outcome (network error,
    // 403 challenge, 5xx) is an unclear write: never re-posted, the final read decides. Nothing is ever
    // "restored": a line that is not in the cart is reported, not written again (the shopper may have removed it
    // in the drawer in the meantime).
    //
    // The cart is read right before posting: an extra whose product is already in the cart is skipped (the
    // Liquid in-cart filter only runs at page render), and the snapshot is the baseline for reconciliation.
    //
    // options.main = { key }: the key of the main line as /cart/add.js answered it. After the last write the
    // cart is read ONCE and that read alone classifies the main line and every posted extra, by presence (not by
    // a quantity: the shopper may have changed it in the drawer), whatever was answered before. Three outcomes:
    //   verified          the main line and every posted extra are in that read (also after a lost response).
    //   verified missing  the read does not show a line the server had answered for: an extra is `failed`
    //                     (reason 'sold_out' after a 422, 'lost' after a 200), the main line sets `mainLost`
    //                     (a line can vanish although its POST answered 200: a concurrent third-party
    //                     /cart/update.json, or the shopper).
    //   uncertain         the deadline hit (the in-flight request is aborted), the final read was refused, or
    //                     an extra got no answer and is not in the read (its write may still land). `uncertain`
    //                     is set and no claim is made either way: `added` holds only lines the read showed,
    //                     undecided extras are in neither list.
    //
    // The main line has three states. EVERY successful cart read (pre-read, 422 recovery read, final read)
    // updates it in read order, so the latest successful read decides:
    //   missing  (`mainLost`) that read does not show the line.
    //   unknown  (`mainUnknown`) that read shows it but is not the final read: it can still be wiped after it
    //            (Bird connector race). Also the state before any read succeeded.
    //   present  the final read shows it.
    // Only `mainLost` withholds the main add_to_cart (snippets/datalayer-gtm.liquid).
    //
    // options.prime = { member, variantId, known }: card price must equal cart price. With the real Prime state
    // from that snapshot (or `known` when the cart cannot be read), an extra the cart would charge MORE for than
    // its card showed is not posted: failed with reason 'price' (the shopper can add it from the cart, at the
    // price shown there). Charging less than shown is fine.
    addExtras(requested, parentKey, io, options) {
      const deadlineMs = (options && options.deadlineMs) || logic.EXTRAS_DEADLINE_MS;
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const signal = controller ? controller.signal : undefined;
      const items = requested.map((item) => ({
        id: item.id,
        quantity: 1,
        properties: Object.assign({}, item.properties, { _upsell_parent: parentKey }),
      }));
      const productOf = {};
      const pricedOf = {};
      requested.forEach((item) => {
        productOf[item.id] = item.product;
        pricedOf[item.id] = item;
      });
      const isOk = (res) => !!(res && res.ok && res.body && Array.isArray(res.body.items));
      const statusOf = (res) => Number((res && res.body && res.body.status) || (res && res.status) || 0);

      const result = { added: [], failed: [], skipped: [], cart: null, timedOut: false, mainLost: false, mainUnknown: false, uncertain: false };
      const mainKey = options && options.main && options.main.key;
      let mainState = 'unknown';
      const seeMain = (cart, final) => {
        if (!mainKey) return;
        const has = ((cart && cart.items) || []).some((line) => line.key === mainKey);
        mainState = !has ? 'missing' : final ? 'present' : 'unknown';
      };
      let baselineQty = null;
      const open = {}; // variant id -> true while its outcome is unknown
      items.forEach((item) => {
        open[item.id] = true;
      });
      let done = false;
      const added = (id, line) => {
        if (done || !open[id]) return;
        delete open[id];
        result.added.push(line);
      };
      const failed = (id, reason, message) => {
        if (done || !open[id]) return;
        delete open[id];
        result.failed.push({ id, reason, message: message || '' });
      };
      const proven = {}; // variant id -> the final cart READ showed the line (an answered POST alone is not proof)
      // What the server answered per posted extra: { line } after a 200, { rejected, message } after a 422,
      // nothing when no answer came (network error, abort, 403, 5xx: that write may still land)
      const answer = {};
      let posted = [];

      let batch = items;
      const work = () =>
        Promise.resolve()
          .then(() => io.getCart(signal))
          .then(
            (cart) => cart,
            () => null // cart read refused: go on without the check
          )
          .then((cart) => {
            if (done) return null;
            if (cart) {
              result.cart = cart;
              seeMain(cart, false);
              baselineQty = {};
              (cart.items || []).forEach((line) => {
                baselineQty[line.key] = line.quantity;
              });
              const inCart = logic.cartState(cart).products;
              batch = items.filter((item) => {
                if (!productOf[item.id] || !inCart[productOf[item.id]]) return true;
                delete open[item.id];
                result.skipped.push(item.id);
                return false;
              });
            }
            const prime = options && options.prime;
            if (prime) {
              const primeNow = cart
                ? !!(prime.member || logic.cartState(cart, prime.variantId).hasPrime)
                : !!(prime.member || prime.known);
              batch = batch.filter((item) => {
                const priced = pricedOf[item.id];
                if (priced.shown == null || logic.charged(priced, primeNow) <= priced.shown) return true;
                failed(item.id, 'price');
                return false;
              });
            }
            if (!batch.length) return 'nothing to post';
            posted = batch;
            return io.post(batch, signal).then(
              (res) => res,
              () => null
            );
          })
          .then((res) => {
            if (done || res === 'nothing to post') return null;
            if (isOk(res)) {
              const lines = res.body.items;
              batch.forEach((item, i) => {
                const line = lines.find((candidate) => String(candidate.variant_id) === String(item.id));
                answer[item.id] = { line: line || lines[i] || { variant_id: item.id, properties: item.properties } };
              });
              return null;
            }
            // Unclear write (network error, 403 challenge, 5xx): never re-posted, the final read decides
            if (statusOf(res) !== 422) return null;
            // Definite rejection of the batch. Part of it may still have landed: read the cart once so nothing is
            // posted twice, then retry what is missing one by one, serially (Shopify locks cart writes per cart)
            return io.getCart(signal).then((cart) => {
              seeMain(cart, false);
              const state = logic.landed(batch, cart && cart.items, parentKey, baselineQty);
              state.found.forEach((pair) => {
                answer[pair.item.id] = { line: pair.line };
              });
              return state.missing.reduce(
                (chain, item) =>
                  chain.then(() => {
                    if (done) return null;
                    return io.post([item], signal).then(
                      (one) => {
                        if (isOk(one) && one.body.items.length) answer[item.id] = { line: one.body.items[0] };
                        else if (statusOf(one) === 422) {
                          answer[item.id] = { rejected: true, message: one.body && one.body.description };
                        }
                      },
                      () => null
                    );
                  }),
                Promise.resolve()
              );
            });
          });

      // The ONE read after the last write classifies the main line and EVERY posted extra, whatever was answered
      // before it: in the cart = added; absent after a 422 = failed (sold out); absent after a 200 = failed
      // (lost); absent with no answer = left undecided (finish() reports uncertain). A refused read rejects.
      const verify = () => {
        if (done || (!posted.length && !mainKey)) return null;
        return io.getCart(signal).then((cart) => {
          if (done) return;
          seeMain(cart, true);
          const lines = (cart && cart.items) || [];
          const byKey = (key) => lines.find((line) => line.key === key);
          const unkeyed = [];
          posted.forEach((item) => {
            const known = answer[item.id];
            const line = known && known.line && known.line.key ? byKey(known.line.key) : null;
            if (line) {
              proven[item.id] = true;
              added(item.id, line);
            } else {
              unkeyed.push(item);
            }
          });
          logic.landed(unkeyed, lines, parentKey, baselineQty).found.forEach((pair) => {
            proven[pair.item.id] = true;
            added(pair.item.id, pair.line);
          });
          posted.forEach((item) => {
            const known = answer[item.id];
            if (known && known.rejected) failed(item.id, 'sold_out', known.message);
            else if (known && known.line) failed(item.id, 'lost');
          });
        });
      };

      return new Promise((resolve) => {
        let timer = null;
        const finish = (unsure) => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          // A 422 is definite even when the final read never answered
          posted.forEach((item) => {
            const known = answer[item.id];
            if (!open[item.id] || !known || !known.rejected) return;
            delete open[item.id];
            result.failed.push({ id: item.id, reason: 'sold_out', message: known.message || '' });
          });
          if (unsure || Object.keys(open).length) result.uncertain = true;
          result.mainLost = !!mainKey && mainState === 'missing';
          result.mainUnknown = !!mainKey && mainState === 'unknown';
          resolve(result);
        };
        timer = setTimeout(() => {
          result.timedOut = true;
          finish(true);
          if (controller) controller.abort();
        }, deadlineMs);
        work()
          .then(verify, verify)
          .then(
            () => finish(false),
            () => finish(true)
          );
      });
    },

    // Index of the first letter or digit of a button label ("🤍 Add to cart" -> 3), 0 when it starts with one
    // or has none. What precedes it (an emoji and a space) is what the sticky total is indented by.
    labelStart(text) {
      const index = String(text || '').search(/[A-Za-z0-9\u00C0-\u024F]/);
      return index > 0 ? index : 0;
    },
  };

  /* ------------------------------------------------------------------ *
   * Cart drawer grids (theme setting upsell_drawer_mode = new), pure logic:
   * unit-tested in tests/ddm-upsell.test.js
   * ------------------------------------------------------------------ */

  // Which tiles of grid 2 (page suggestions) may show. tiles = [{ productId, leadHours, nextDayOnly, rental }],
  // context = { exclude: { productId: true }, cartLead: hours or null, cartSameDay, cartRental }.
  //   exclude     - products that are in the cart or already in grid 1
  //   cartLead    - the cart's lead time (its slowest line). A tile with a longer lead would push the delivery
  //                 date: hidden. null = no check.
  //   cartSameDay - true while the cart can still go the same day: a next-day-only tile would push it to
  //                 tomorrow: hidden. null / false = no check.
  //   cartRental  - true while the cart has a `rental` line: only then may a rental tile show
  // Weekly closed days do NOT hide a tile (user ruling): the page's own suggestions stay, as on Live (a DodoFresh
  // tile on a DodoFresh page with a gift-only cart); pre-checkout explains closed days.
  logic.gridEligible = function (tiles, context) {
    const exclude = (context && context.exclude) || {};
    const cartLead = context && context.cartLead;
    return tiles.map((tile) => {
      if (exclude[tile.productId]) return false;
      if (tile.rental && !(context && context.cartRental)) return false;
      if (cartLead != null && (tile.leadHours || 0) > cartLead) return false;
      return !(context && context.cartSameDay && tile.nextDayOnly);
    });
  };

  // The legacy grid paging: the first `limit` eligible tiles show, "Load more" while eligible ones are left
  logic.gridPage = function (eligible, limit) {
    let shown = 0;
    const visible = eligible.map((ok) => {
      if (!ok || shown >= limit) return false;
      shown += 1;
      return true;
    });
    return { visible, more: eligible.filter(Boolean).length > shown };
  };

  // Tile price row, as the legacy grid paints it: member price + list price when the cart has DodoPrime benefits
  // and the product carries a prime-N tag, else sale price + compare-at price, else the price alone.
  logic.gridPrice = function (tile, primeCart) {
    if (primeCart && tile.primeDiscount > 0) {
      return { price: Math.round((tile.price * (100 - tile.primeDiscount)) / 100), compare: tile.price };
    }
    if (tile.comparePrice > tile.price) return { price: tile.price, compare: tile.comparePrice };
    return { price: tile.price, compare: 0 };
  };

  // May this tap post? { inert, shown, charge }: shown = the price painted on the tile, charge = what the cart
  // would charge under the Prime state of the most recently rendered cart.
  //   'ignore' - an add of the grids' own is running (no double taps, no queue)
  //   'price'  - the cart would charge more than the tile shows (or the tile price is unknown): never posted
  //   'post'
  logic.gridTap = function (tap) {
    if (tap.inert) return 'ignore';
    return tap.shown >= tap.charge ? 'post' : 'price';
  };

  // One tap = ONE POST /cart/add.js, bounded by the deadline.
  // Resolves, exactly once, with { outcome: 'added' | 'rejected' | 'uncertain', line, message, timedOut }.
  //   added     - the cart answered with the line
  //   rejected  - Shopify refused it (422, e.g. sold out): nothing was written, message = its description
  //   uncertain - network error, 403 challenge, 5xx or deadline: the write may or may not be in the cart.
  //               It is never posted again; the cart the drawer rebuild fetches decides (logic.lineLanded).
  // Never rejects. io = { post(items, signal) -> { ok, status, body } }.
  logic.drawerWrite = function (request, io, options) {
    const deadlineMs = (options && options.deadlineMs) || EXTRAS_DEADLINE_MS;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const work = () =>
      Promise.resolve()
        .then(() =>
          io.post(
            [{ id: request.id, quantity: 1, properties: request.properties }],
            controller ? controller.signal : undefined
          )
        )
        .then((res) => {
          const body = res && res.body;
          if (res && res.ok && body && Array.isArray(body.items) && body.items.length) {
            return { outcome: 'added', line: body.items[0] };
          }
          const status = Number((body && body.status) || (res && res.status) || 0);
          if (status === 422) return { outcome: 'rejected', message: (body && body.description) || '' };
          return { outcome: 'uncertain' };
        });

    return new Promise((resolve) => {
      let done = false;
      let timer = null;
      const finish = (result) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(Object.assign({ outcome: 'uncertain', line: null, message: '', timedOut: false }, result));
      };
      timer = setTimeout(() => {
        finish({ timedOut: true });
        if (controller) controller.abort();
      }, deadlineMs);
      work().then(finish, () => finish({}));
    });
  };

  // Is the write in the cart the drawer rebuild fetched? keys = the line keys of that cart ("<variant id>:<hash>").
  // Presence of the line key decides for a confirmed add (a third-party script can remove a just-added line);
  // an uncertain one has no key and is looked up by its variant (the product was not in the cart before, or it
  // would not have been offered).
  logic.lineLanded = function (keys, entry) {
    if (entry.key) return keys.indexOf(entry.key) !== -1;
    const prefix = String(entry.variantId) + ':';
    return keys.some((key) => String(key).indexOf(prefix) === 0);
  };

  // Live region text after the drawer was rebuilt: "Added X. Added Y. Cart total Rs Z."
  // total = formatted cart total, '' when the rebuild did not arrive in time.
  logic.drawerStatus = function (titles, total, strings) {
    const parts = titles.map((title) => logic.fill(strings.statusAdded, { title }));
    if (parts.length && total) parts.push(logic.fill(strings.statusTotal, { total }));
    return parts.join(' ');
  };

  if (!document) {
    // Node test harness: logic only
    window.DDMUpsell = { _logic: logic };
    return;
  }

  /* ------------------------------------------------------------------ *
   * DOM helpers
   * ------------------------------------------------------------------ */
  const SEL = {
    block: '[data-upsell-block]',
    option: '[data-upsell-option]',
    checkbox: '[data-upsell-checkbox]',
    variant: '[data-upsell-variant]',
    config: '[data-upsell-config]',
    debug: '[data-upsell-debug]',
    atcTotal: '[data-upsell-atc-total]',
  };
  const ROW_MAX = 3;
  const states = new WeakMap();

  function parseJSON(el) {
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function money(cents) {
    const theme = window.theme;
    if (theme && theme.Currency && theme.Currency.formatMoney) {
      const holder = document.createElement('div');
      holder.innerHTML = theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      return holder.textContent;
    }
    return 'Rs ' + Math.round(cents / 100).toLocaleString('en-US');
  }

  // Quick view clones the product blocks into a modal: the block is hidden there (CSS) and ignored here
  function inModal(el) {
    return !!el.closest('.modal');
  }

  function allBlocks() {
    return Array.from(document.querySelectorAll(SEL.block)).filter((block) => !inModal(block));
  }

  function stateFor(block) {
    if (!block || inModal(block)) return null;
    let state = states.get(block);
    if (state) return state;
    const configEl = block.querySelector(SEL.config);
    const config = configEl && parseJSON(configEl);
    if (!config || !config.main || !Array.isArray(config.items)) return null;
    const options = [];
    block.querySelectorAll(SEL.option).forEach((option) => {
      options[Number(option.dataset.index)] = option;
    });
    // Strings come as data-t-* attributes: the browser decodes the HTML escaping Liquid's `t` applies, which a
    // JSON payload would carry through as literal "&#39;" (dataset.tFailedSoldOut -> i18n.failedSoldOut)
    const i18n = {};
    Object.keys(block.dataset).forEach((key) => {
      const match = /^t([A-Z])(.*)$/.exec(key);
      if (match) i18n[match[1].toLowerCase() + match[2]] = block.dataset[key];
    });
    state = {
      block,
      config,
      i18n,
      options,
      rowMax: config.rowMax || ROW_MAX,
      variantPrice: config.main.price,
      available: config.main.available !== false,
      waiting: false,
      notice: '',
      // Live Prime signals: member is fixed for the page, inCart follows the cart (see syncCart / markInCart)
      prime: {
        member: !!(config.prime && config.prime.member),
        inCart: !!(config.prime && config.prime.inCart),
        variantId: (config.prime && config.prime.variantId) || 0,
      },
      paintedPrime: !!(config.prime && (config.prime.member || config.prime.inCart)), // what Liquid painted
      // Pool product ids that are in the cart NOW. Liquid drops in-cart products at render only; this follows
      // every cart change on the page (own adds, drawer adds and removals), so a card never offers them twice.
      inCart: {},
    };
    states.set(block, state);
    return state;
  }

  function stateForForm(form) {
    const id = logic.formId(form);
    if (!id) return null;
    const block = allBlocks().find((candidate) => {
      const state = stateFor(candidate);
      return state && state.config.formId === id;
    });
    return block ? stateFor(block) : null;
  }

  function formOf(state) {
    return document.getElementById(state.config.formId);
  }

  function checkboxOf(option) {
    return option ? option.querySelector(SEL.checkbox) : null;
  }

  function flagsOf(state) {
    return state.config.items.map((item, i) => {
      const checkbox = checkboxOf(state.options[i]);
      return {
        checked: !!(checkbox && checkbox.checked),
        disabled: !!(checkbox && checkbox.disabled),
        inCart: !!state.inCart[item.p],
      };
    });
  }

  // The PDP "add DodoPrime" opt-in (snippets/pdp-prime-upsell.liquid): ticked = Prime joins the cart in this
  // submission, so the cart will charge member prices. Once that submit ran (form.__primeUpsellAdded) only the
  // cart counts: a failed Prime add must not keep member prices on the cards.
  function primeOptIn(state) {
    const optIn = Array.from(document.querySelectorAll('[data-prime-upsell-cb]')).find((el) => !inModal(el));
    const form = formOf(state);
    return !!(optIn && optIn.checked && !(form && form.__primeUpsellAdded));
  }

  function primeOnFor(state) {
    return logic.primeOn({ member: state.prime.member, inCart: state.prime.inCart, optIn: primeOptIn(state) });
  }

  // Visible stepper (optional quantity_selector block, form-linked) wins over the hidden in-form input,
  // the same pair theme.StickyAddToCart keeps in sync
  function quantityOf(state) {
    const form = formOf(state);
    const visible = document.querySelector('input.js-qty__num[name="quantity"][form="' + state.config.formId + '"]');
    const hidden = form && form.querySelector('[data-product-quantity]');
    const input = visible || hidden;
    return (input && parseInt(input.value, 10)) || 1;
  }

  // An enabled, empty personalisation / option field: the theme's own validation will block the add
  function missingInput(form) {
    if (!form) return false;
    const text = form.elements.namedItem('properties[Personalization]');
    const select = form.elements.namedItem('properties[Selected Option]');
    const textMissing = text && !text.disabled && !String(text.value || '').trim();
    const selectMissing = select && !select.disabled && !select.value;
    return !!(textMissing || selectMissing);
  }

  function totalSpans(state) {
    const form = formOf(state);
    const spans = [];
    const main = form && form.querySelector(SEL.atcTotal);
    if (main) spans.push(main);
    const sticky = document.querySelector(
      '[data-sticky-atc-btn][data-form-id="' + state.config.formId + '"] ' + SEL.atcTotal
    );
    if (sticky) spans.push(sticky);
    return spans;
  }

  /* ------------------------------------------------------------------ *
   * Render: visibility, card prices, summary line, running total
   * ------------------------------------------------------------------ */
  function refresh(state) {
    const items = state.config.items;
    const i18n = state.i18n;
    // A product that reached the cart is no longer an offer: untick it before anything is computed
    items.forEach((item, i) => {
      const checkbox = state.inCart[item.p] ? checkboxOf(state.options[i]) : null;
      if (checkbox) checkbox.checked = false;
    });
    const flags = flagsOf(state);
    const primeOn = primeOnFor(state);
    const visible = logic.visibility(items, flags, state.rowMax);

    // Card price = cart price: re-price the cards (and the option prices of their fields) when the Prime state
    // differs from what is painted
    if (primeOn !== state.paintedPrime) {
      items.forEach((item, i) => paintPrice(state, state.options[i], item, primeOn));
      state.paintedPrime = primeOn;
    }

    const anyVisible = visible.some(Boolean);
    const list = state.block.querySelector('.upsell-block__list');
    const applyVisibility = () => {
      state.options.forEach((option, i) => {
        if (option) option.hidden = !visible[i];
      });
    };
    if (anyVisible) {
      showList(list);
      applyVisibility();
    } else {
      // Last offer gone (it is in the cart): only the card list collapses, the block keeps its heading and the
      // result line ("Added to cart with 1 extra")
      collapseList(list, applyVisibility);
    }

    const sums = logic.totals(
      logic.mainUnit(state.variantPrice, state.config.main, primeOn),
      quantityOf(state),
      items,
      flags,
      primeOn
    );

    // Summary: "2 extras selected · Rs 1,320", the waiting copy while a required field is empty,
    // or the result of the last add until the shopper touches the block again
    const summary = state.block.querySelector('[data-upsell-summary]');
    const summaryText = state.block.querySelector('[data-upsell-summary-text]');
    if (summary && summaryText) {
      let text = state.notice;
      let waiting = false;
      if (sums.count > 0) {
        waiting = state.waiting && missingInput(formOf(state));
        const one = waiting ? i18n.waitingOne : i18n.summaryOne;
        const other = waiting ? i18n.waitingOther : i18n.summaryOther;
        text = logic.fill(sums.count === 1 ? one : other, { count: sums.count, price: money(sums.extras) });
      }
      summaryText.textContent = text;
      state.block.classList.toggle('upsell-block--has-status', !!text);
      // Nothing to offer and nothing to say: the block goes
      state.block.hidden = !anyVisible && !text;
    }

    // Running total on the add-to-cart button and the mobile sticky bar, only while an extra is ticked
    const showTotal = sums.count > 0 && state.available;
    totalSpans(state).forEach((span) => {
      span.textContent = showTotal ? money(sums.total) : ''; // the "· " on the main button is CSS
      span.hidden = !showTotal;
      if (showTotal) indentUnderLabel(span);
    });

    return sums;
  }

  // The card's "+Rs ..." and, when the variants differ in price, each option's "Title · Rs ..."
  function paintPrice(state, option, item, primeOn) {
    if (!option) return;
    const price = option.querySelector('[data-upsell-price]');
    if (price) {
      // The selected variant sold out while others are left: the price slot says so (syncSoldOut)
      const out = option.classList.contains('upsell-option--variant-out');
      price.textContent = out ? state.i18n.soldOut || '' : '+' + money(logic.charged(item, primeOn));
    }
    const select = item.variantPrices ? option.querySelector(SEL.variant) : null;
    if (!select) return;
    Array.from(select.options).forEach((el) => {
      const variant = item.variants.find((candidate) => String(candidate.v) === el.value);
      if (variant) el.textContent = variantText(state, item, variant, primeOn);
    });
  }

  // Option text: the variant title, its price when the variants differ in price, "(sold out)" once it sold out
  function variantText(state, item, variant, primeOn) {
    const text = item.variantPrices ? variant.title + ' · ' + money(logic.charged(variant, primeOn)) : variant.title;
    return variant.soldOut ? logic.fill(state.i18n.variantSoldOut, { title: text }) : text;
  }

  /* ------------------------------------------------------------------ *
   * Sold out between render and add (settle). No dead end: a card with a variant field loses only the rejected
   * variant (its option disabled and marked, the field stays usable); while that variant is selected only the
   * checkbox is disabled and the price slot says "Sold Out", and picking a stocked one frees it again (onInput).
   * Without a field, or with no variant left, the whole card is dimmed and locked. The state lives on
   * item.variants[].soldOut and the DOM: refresh and cart events never touch it.
   * Returns true when the card has other variants left (the sentence then says "Choose another option").
   * ------------------------------------------------------------------ */
  function markSoldOut(state, index, variantId) {
    const option = state.options[index];
    const item = state.config.items[index];
    if (!option || !item) return;
    const select = option.querySelector(SEL.variant);
    const variant = (item.variants || []).find((candidate) => String(candidate.v) === String(variantId));
    if (select && variant) {
      variant.soldOut = true;
      const el = Array.from(select.options).find((candidate) => candidate.value === String(variant.v));
      if (el) {
        el.disabled = true;
        el.textContent = variantText(state, item, variant, state.paintedPrime);
      }
    }
    if (select && (!variant || item.variants.every((candidate) => candidate.soldOut))) select.disabled = true;
    const locked = !select || select.disabled;
    syncSoldOut(state, option, item, locked);
    return !locked;
  }

  // Whole card locked (`locked`): dimmed, "Sold Out" line. Selected variant sold out, others left: only the checkbox
  // is disabled and the price slot says "Sold Out"; no line comes or goes, so the card keeps its height (QA D2, D3).
  function syncSoldOut(state, option, item, locked) {
    const current = (item.variants || []).find((candidate) => String(candidate.v) === String(item.v));
    const variantOut = !locked && !!(current && current.soldOut);
    const checkbox = checkboxOf(option);
    if (checkbox) checkbox.disabled = !!locked || variantOut;
    option.classList.toggle('upsell-option--locked', !!locked);
    option.classList.toggle('upsell-option--variant-out', variantOut);
    const meta = option.querySelector('[data-upsell-meta]');
    if (meta) {
      meta.textContent = locked ? state.i18n.soldOut || '' : '';
      meta.hidden = !locked;
    }
    paintPrice(state, option, item, state.paintedPrime);
  }

  // Sticky bar: the total is on its own line under a label that starts with an emoji ("🤍 Add to cart").
  // It must start where the label TEXT starts. The emoji's width depends on the platform font, so the offset is
  // measured; assets/custom.css applies it (--upsell-label-indent) in the stacked layout only.
  function indentUnderLabel(span) {
    const label = span.parentNode && span.parentNode.querySelector('[data-sticky-atc-text]');
    const node = label && label.firstChild;
    if (!node || node.nodeType !== 3) return;
    const range = document.createRange();
    range.setStart(node, 0);
    range.setEnd(node, logic.labelStart(node.data));
    const width = typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect().width : 0;
    span.style.setProperty('--upsell-label-indent', Math.round(width * 100) / 100 + 'px');
  }

  function showList(list) {
    if (!list) return;
    if (list.upsellCollapse) {
      list.upsellCollapse.cancel();
      list.upsellCollapse = null;
      list.style.overflow = '';
    }
    list.hidden = false;
  }

  // Collapse the card list, smoothly when motion is welcome, so the content below does not jump
  function collapseList(list, done) {
    if (!list || list.hidden) return done();
    if (list.upsellCollapse) return undefined; // already on its way
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof list.animate !== 'function') {
      list.hidden = true;
      return done();
    }
    list.style.overflow = 'hidden';
    const animation = list.animate(
      [
        { height: list.offsetHeight + 'px', opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 250, easing: 'ease' }
    );
    list.upsellCollapse = animation;
    animation.onfinish = () => {
      list.upsellCollapse = null;
      list.style.overflow = '';
      list.hidden = true;
      done();
    };
    return undefined;
  }

  /* ------------------------------------------------------------------ *
   * Snapshot + lock when a valid main submission starts (Codex pass 17)
   * ------------------------------------------------------------------ */
  // Immutable copy of what the extras add will post: variant ids, line properties, and the prices shown on the cards
  // now (`shown`, what the price guard compares against). The card prices are the ones PAINTED: when the PDP Prime
  // pre-add ran first and failed, the card still shows the member price the shopper saw when pressing add.
  function snapshotOf(state) {
    return logic.selection(state.config.items, flagsOf(state), state.rowMax, state.paintedPrime).map((entry) =>
      Object.freeze(Object.assign({}, entry, { properties: Object.freeze(Object.assign({}, entry.properties)) }))
    );
  }

  function lock(state, snapshot) {
    clearTimeout(state.lockTimer);
    state.snapshot = snapshot;
    setBusy(state, true);
    // A main add still unanswered at the deadline must not freeze the page: the UI is released, but the snapshot
    // stays until the main add resolves. A late success still adds what was pressed (afterMainAdd); a main failure
    // clears it (onMainError); a new valid press replaces it (onSubmit).
    state.lockTimer = setTimeout(() => {
      state.lockTimer = null;
      setBusy(state, false);
    }, logic.EXTRAS_DEADLINE_MS);
  }

  // Every path ends here: extras settled, nothing to add, main add failed or missing, exception, extras timeout
  function unlock(state) {
    clearTimeout(state.lockTimer);
    state.lockTimer = null;
    state.snapshot = null;
    state.preAdd = false;
    setBusy(state, false);
  }

  // A valid main submission STARTS. This listener is on document, bubble phase, so it runs right after the form's own
  // submit listener, theme.AjaxProduct.addItemFromForm (assets/theme.js), in the same task. That handler validates
  // the required fields and, only when it posts the main item, puts the form's .add-to-cart button into its loading
  // state: an idle button means a validation stop (the "waiting" copy) or another form's add still running, so
  // nothing is taken. The sticky bar submits the same form (requestSubmit); the PDP Prime pre-add stops its first
  // submit and submits again: both arrive here once, when the main POST really starts.
  // A press after a deadline release, while this form's main add is still pending: the theme ignores it (its shared
  // loading flag, the button still spins) but the shopper restated what they want, so a press that passes the
  // theme's validation (missingInput mirrors it) replaces the snapshot and locks again.
  function onSubmit(event) {
    const form = event.target;
    const state = form && form.tagName === 'FORM' ? stateForForm(form) : null;
    if (!state) return;
    const button = form.querySelector('.add-to-cart');
    const posting = !!(button && button.classList.contains('btn--loading'));
    if (state.preAdd) {
      // Only the Prime pre-add's own automatic resubmit ends it: the Prime handler (snippets/product-template.liquid)
      // flags it with form.__upsellResubmit around its requestSubmit. Any other submit leaves the pre-add and its
      // snapshot alone (the handler consumes the shopper's presses meanwhile).
      if (!form.__upsellResubmit) return;
      // The snapshot from the press stays, never re-captured. Main POST started: the lock (and its deadline) now runs
      // for it. Stopped by the theme's validation or another add: nothing will follow, so the block is freed.
      state.preAdd = false;
      if (posting) lock(state, state.snapshot);
      else unlock(state);
      return;
    }
    if (state.busy) return; // busy: locked, nothing in the block can have changed
    if (!posting || missingInput(form)) return;
    lock(state, snapshotOf(state));
  }

  // The PDP Prime pre-add (snippets/product-template.liquid) takes the shopper's FIRST press, adds DodoPrime and
  // submits the form again when its requests are done. The press is the shopper's choice: the snapshot is taken and
  // the block locked now, before those requests, and kept through the resubmit (onSubmit). A press the theme's
  // validation would refuse (missingInput mirrors it) takes nothing. Returns true when a snapshot was taken.
  function beginSubmission(form) {
    const state = stateForForm(form);
    if (!state || state.busy || missingInput(form)) return false;
    lock(state, snapshotOf(state));
    state.preAdd = true;
    return true;
  }

  // The pre-add ended without resubmitting (cancelled): nothing will follow
  function endSubmission(form) {
    const state = stateForForm(form);
    if (!state || !state.preAdd) return;
    state.preAdd = false;
    unlock(state);
  }

  // The main add failed or was rejected (theme.js error(): non-2xx, 422, network): no extras POST follows
  function onMainError(event) {
    const form = event.target;
    const state = form && form.tagName === 'FORM' ? stateForForm(form) : null;
    if (state && state.snapshot) unlock(state);
  }

  // Cards are locked while the extras are being added: a tick made then could not be honoured
  function setBusy(state, busy) {
    state.busy = busy;
    state.block.classList.toggle('upsell-block--busy', busy);
    const list = state.block.querySelector('.upsell-block__list');
    if (!list) return;
    if (busy) list.setAttribute('inert', '');
    else list.removeAttribute('inert');
  }

  function announce(state, text) {
    const status = state.block.querySelector('[data-upsell-status]');
    if (status) status.textContent = text;
  }

  /* ------------------------------------------------------------------ *
   * Add sequence (called by theme.js after the main add succeeded)
   * ------------------------------------------------------------------ */
  function routes() {
    const themeRoutes = (window.theme && window.theme.routes) || {};
    return { cartAdd: themeRoutes.cartAdd || '/cart/add.js', cart: themeRoutes.cart || '/cart.js' };
  }

  const io = {
    post(items, signal) {
      return fetch(routes().cartAdd, {
        method: 'POST',
        signal,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      }).then((response) =>
        response.json().then(
          (body) => ({ ok: response.ok, status: response.status, body }),
          () => ({ ok: false, status: response.status, body: null }) // 403 bot challenge returns HTML
        )
      );
    },
    getCart(signal) {
      return fetch(routes().cart + '?t=' + Date.now(), {
        signal,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((response) => {
        if (!response.ok) throw new Error('cart ' + response.status);
        return response.json();
      });
    },
  };

  function settle(state, requested, result) {
    // The snapshot taken at the press names the extras: after a late main answer the card may show another variant.
    // presentResult (also when reviseFromRender rewrites the result) reads titles and urls from result.entryOf.
    const byVariant = {};
    const entryOf = {};
    requested.forEach((item) => {
      byVariant[item.id] = item.index;
      entryOf[item.id] = item;
    });
    result.byVariant = byVariant;
    result.entryOf = entryOf;

    // A sold-out extra can no longer be ticked: the variant alone when the card has other available variants
    (result.failed || []).forEach((failure) => {
      if (failure.reason !== 'sold_out') return;
      // Other variants left: presentResult's sentence sends the shopper to the field (Staging QA D2)
      failure.alternatives = markSoldOut(state, byVariant[failure.id], failure.id);
    });

    // The snapshot's cards are done. A tick made after a deadline release is not part of this add: it stays.
    requested.forEach((item) => {
      const checkbox = checkboxOf(state.options[item.index]);
      if (checkbox) checkbox.checked = false;
    });

    // What is in the cart now: the pre-post snapshot, plus what this add put there
    if (result.cart) syncCart(state, result.cart);
    (result.added || []).forEach((line) => markInCart(state, line));
    (result.skipped || []).forEach((id) => {
      const item = state.config.items[byVariant[id]];
      if (item) state.inCart[item.p] = true;
    });

    state.result = result; // the drawer build of this add may still contradict it (reviseFromRender)
    state.noticeAt = renderSeq; // the drawer build of this add renders next; any render after that expires the line
    presentResult(state, result);

    // Uncertain outcome: `added` holds only the lines a cart read showed
    (result.added || []).forEach(track);

    return result;
  }

  // What the block (status line, live region) and the drawer alert say about an add's result
  function presentResult(state, result) {
    const i18n = state.i18n;
    // No dead ends: every outcome says what happened and what to do. A main item the cart does not hold wins
    // over everything (it is the one thing to act on). A main item no read confirmed is never called "added":
    // the shopper is sent to check the whole cart. Only with the main item confirmed do the extras get their own
    // line.
    let status = '';
    let lead = '';
    if (result.mainLost) status = lead = i18n.failedMain || '';
    else if (result.mainUnknown) [status, lead] = [i18n.statusCheckCart || '', i18n.alertCheckCart || ''];
    else if (result.uncertain) [status, lead] = [i18n.statusUnconfirmed || '', i18n.alertExtrasUnconfirmed || ''];

    // Sentences for the drawer alert, the main item first. An extra that was not added says why; where the
    // sentence sends the shopper to its product page, its title links there (drawerAlert).
    const alert = lead ? [lead] : [];
    (result.failed || []).forEach((failure) => {
      const item = result.entryOf[failure.id] || {};
      if (failure.reason === 'sold_out') alert.push(logic.fill(failure.alternatives ? i18n.failedSoldOutChoose : i18n.failedSoldOut, { title: item.title || '' }));
      else alert.push({ template: failure.reason === 'price' ? i18n.failedPrice : i18n.failedLost, title: item.title || '', url: item.url });
    });
    result.alert = alert;
    result.messages = alert.map((entry) => (typeof entry === 'string' ? entry : logic.fill(entry.template, { title: entry.title })));

    // Status line: ONE short line (tests/upsell-strings.test.js). Counts, not sentences; the sentences are in
    // the drawer alert and the live region.
    const addedCount = (result.added || []).length;
    const failedCount = (result.failed || []).length;
    const added = addedCount
      ? logic.fill(addedCount === 1 ? i18n.addedWithOne : i18n.addedWithOther, { count: addedCount })
      : '';
    if (status) state.notice = status;
    else if (addedCount && failedCount) state.notice = logic.fill(i18n.mixed, { added: addedCount, failed: failedCount });
    else if (failedCount) state.notice = logic.fill(failedCount === 1 ? i18n.notAddedOne : i18n.notAddedOther, { count: failedCount });
    else state.notice = added;
    state.waiting = false;
    refresh(state);
    // Live region: only what is true. With the main item lost, "Added to cart with 1 extra" is not the news.
    announce(state, status || [added].concat(result.messages).filter(Boolean).join('. ').replace(/\.\. /g, '. '));
  }

  // The drawer build of an add can show a cart that contradicts its result: a competing write (the Bird connector
  // race) can wipe lines between the verifying read and that build. Then the block and the drawer alert entry of
  // that add say what this cart shows: main line gone -> "Your item wasn't added"; an added extra gone -> the
  // failed_lost sentence with its product link.
  function reviseFromRender(state, cart) {
    const result = state.result;
    const keys = (cart.items || []).map((line) => line.key);
    const has = (key) => keys.indexOf(key) !== -1;
    let changed = false;
    if (result.mainKey && !result.mainLost && !has(result.mainKey)) {
      result.mainLost = true;
      changed = true;
    }
    const gone = (result.added || []).filter((line) => !has(line.key));
    if (gone.length) {
      result.added = result.added.filter((line) => has(line.key));
      result.failed = (result.failed || []).concat(gone.map((line) => ({ id: line.variant_id, reason: 'lost', message: '' })));
      changed = true;
    }
    if (!changed) return;
    presentResult(state, result);
    if (result.alertId) setAlert(result.alertId, result.alert);
  }

  /* ------------------------------------------------------------------ *
   * Cart state: which pool products are in the cart, and is DodoPrime in it
   * (no extra requests: own snapshots + cart events)
   * ------------------------------------------------------------------ */
  function syncCart(state, cart) {
    const cartState = logic.cartState(cart, state.prime.variantId);
    state.prime.inCart = cartState.hasPrime;
    state.inCart = {};
    state.config.items.forEach((item) => {
      if (cartState.products[item.p]) state.inCart[item.p] = true;
    });
  }

  function markInCart(state, line) {
    if (!line) return;
    if (line.product_id) state.inCart[line.product_id] = true;
    if (state.prime.variantId && String(line.variant_id) === String(state.prime.variantId)) state.prime.inCart = true;
  }

  // cart:updated carries the whole cart (drawer quantity changes and removals, legacy grid adds);
  // ajaxProduct:added / :tracked carry the line that was just added
  function onCartEvent(event) {
    const detail = event.detail || {};
    // The result line of the last add ("Added to cart with 1 extra", "... couldn't be added") is stale as soon
    // as the cart changes again: a later add without extras, a removal or quantity change in the drawer.
    // Not on ajaxProduct:tracked (our own extras) and not on the add that carries this very result.
    const stale = event.type === 'cart:updated' || (event.type === 'ajaxProduct:added' && !detail.upsell);
    allBlocks().forEach((block) => {
      const state = stateFor(block);
      if (!state) return;
      if (stale && state.notice) {
        state.notice = '';
        announce(state, ''); // the live region must not keep a result the status line no longer shows
      }
      if (event.type === 'ajaxProduct:added' && detail.upsell) state.noticeAt = renderSeq;
      if (event.type === 'cart:updated') {
        if (detail.cart) syncCart(state, detail.cart);
      } else {
        markInCart(state, detail.product);
      }
      refresh(state);
    });
    // The drawer alert too, entry by entry: settled ones are stale; an unresolved tile add stays until a rendered
    // cart says what happened. Not on the cart:updated the drawer grids publish themselves (about their own add).
    if (event.type === 'cart:updated' && !publishing) {
      dropSettledAlerts(() => true);
      drawerAnnounce('');
    }
    renderDebug();
  }

  // Every cart the drawer renders (cart:rendered), also without any cart:updated (theme.js rebuilds after a
  // quantity change whose response was lost): the blocks follow that cart like a cart:updated (in-cart flags,
  // refill). The result line of the last add lasts until the render after its own add's render, like a drawer
  // success. cartJSON comes with drawer mode new only; without it only the result line expires.
  function syncBlocksFromRender(cartJSON) {
    let cart = null;
    try {
      cart = cartJSON ? JSON.parse(cartJSON) : null;
    } catch (e) {
      cart = null;
    }
    allBlocks().forEach((block) => {
      const state = stateFor(block);
      if (!state) return;
      if (state.notice && renderSeq > state.noticeAt + 1) {
        state.notice = '';
        announce(state, '');
      } else if (state.notice && state.result && cart && Array.isArray(cart.items)) {
        reviseFromRender(state, cart);
      }
      if (cart && Array.isArray(cart.items)) syncCart(state, cart);
      refresh(state);
    });
  }

  // Analytics only: never ajaxProduct:added / :error for extras (that would rebuild the drawer per extra,
  // and the gift-card recipient listener reads event.target as a form)
  function track(line) {
    document.dispatchEvent(
      new CustomEvent('ajaxProduct:tracked', {
        detail: { product: line, quantityAdded: 1, atcSource: 'upsell_pdp' },
      })
    );
  }

  // Returns null when there is nothing to add (theme.js then behaves exactly as before),
  // else a promise that resolves { added, failed, messages, mainLost, uncertain } within EXTRAS_DEADLINE_MS and
  // never rejects. Once it resolved the module does nothing more for this add (see logic.addExtras).
  // It adds the snapshot taken when the submission started (onSubmit), never what the block shows now, also when
  // the main answer comes after the deadline released the UI. Without a snapshot (no valid submission seen, or the
  // main add failed) it adds nothing.
  function afterMainAdd(form, mainLine) {
    let state = null;
    try {
      state = stateForForm(form);
      const requested = state && state.snapshot;
      if (!requested) return null;
      state.snapshot = null;
      if (!requested.length || !mainLine || !mainLine.key) {
        unlock(state);
        return null;
      }
      // Locked while the extras are added (again, after a late answer); safety net on top of the module's own
      // deadline (addExtras always settles by then)
      clearTimeout(state.lockTimer);
      setBusy(state, true);
      state.lockTimer = setTimeout(() => unlock(state), logic.EXTRAS_DEADLINE_MS + 1000);
      const prime = { member: state.prime.member, variantId: state.prime.variantId, known: state.prime.inCart };
      return logic
        .addExtras(requested, mainLine.key, io, { prime, main: { key: mainLine.key } })
        .then((result) => {
          unlock(state);
          result.mainKey = mainLine.key;
          return settle(state, requested, result);
        })
        .catch(() => {
          unlock(state);
          return { added: [], failed: [], messages: [], mainLost: false };
        });
    } catch (e) {
      if (state) unlock(state);
      return null;
    }
  }

  /* ------------------------------------------------------------------ *
   * Alert at the top of the open cart drawer: an ordered list with ONE entry per operation (a PDP add's result,
   * a drawer tile add, a tile's refusal or price change). Every outcome sets or removes only its own entry
   * (setAlert, by id) and the alert is re-rendered from the list (drawerAlert is the only DOM writer).
   *   open   - its operation is unresolved (a tile add the deadline left unconfirmed): nothing clears it but the
   *            rendered cart that says what happened
   *   fresh  - set in this tick: the drawer open that belongs to the same add (same tick, listener order unknown)
   *            keeps it; every later open clears the settled entries
   *   render - a success ("Added X."): the cart render that set it. The next cart render removes it, whatever
   *            triggered that render (a later cart may no longer have the line); warnings keep their rules
   * Outcomes only, never a cart total: the drawer subtotal is right there, and the live region announces the
   * latest total with every add (a total kept here goes stale with the next add).
   * ------------------------------------------------------------------ */
  const alerts = [];
  let alertSeq = 0;
  let renderSeq = 0; // cart-drawer renders so far
  let publishing = false; // the drawer grids' own cart:updated is being dispatched
  let alertHold = 0; // > 0: changes are collected and rendered once, when the outermost holdAlerts ends
  let alertShown = '[]'; // what the alert shows now (JSON of the entries)

  function setAlert(id, entries, extra) {
    const at = alerts.findIndex((alert) => alert.id === id);
    if (!entries || !entries.length) {
      if (at !== -1) alerts.splice(at, 1);
    } else {
      const alert = Object.assign({ id, entries, open: false }, extra, { fresh: true });
      if (at === -1) alerts.push(alert);
      else alerts[at] = alert;
      setTimeout(() => {
        alert.fresh = false;
      }, 0);
    }
    showAlerts();
  }

  function dropSettledAlerts(which) {
    for (let i = alerts.length - 1; i >= 0; i -= 1) {
      if (!alerts[i].open && which(alerts[i])) alerts.splice(i, 1);
    }
    showAlerts();
  }

  // ONE render per logical update (a PDP add replacing its entry, a cart render settling tiles), and none when the
  // entries did not change: rewriting the alert moves focus off a focused recovery link (drawerAlert)
  function holdAlerts(update) {
    alertHold += 1;
    try {
      update();
    } finally {
      alertHold -= 1;
    }
    showAlerts();
  }

  function showAlerts() {
    if (alertHold) return;
    const entries = [].concat(...alerts.map((alert) => alert.entries));
    const shown = JSON.stringify(entries);
    if (shown === alertShown) return;
    alertShown = shown;
    drawerAlert(entries);
  }

  // entries: sentences, or { template, title, url } whose [title] becomes a link to the product page
  function drawerAlert(entries) {
    // First thing in .drawer__inner, above the scrolling list: there it only competes with the list for room, the
    // checkout footer keeps its own (custom.css). The theme hides .drawer__inner for an empty cart; the class keeps
    // it, with the alert only, while the alert has text.
    const inner = document.querySelector('#CartDrawer .drawer__inner');
    if (!inner) return;
    let alert = inner.querySelector('.upsell-drawer-alert');
    if (!alert) {
      if (!entries.length) return;
      alert = document.createElement('p');
      alert.className = 'upsell-drawer-alert';
      alert.setAttribute('role', 'alert');
      alert.tabIndex = 0; // a long alert scrolls inside itself (custom.css): keyboard users must be able to scroll it
      inner.insertBefore(alert, inner.firstChild);
    }
    // Focus inside the alert while it is rewritten or emptied: a removed link would drop it to <body>, outside the open
    // drawer and its Tab handling. Keep it in the drawer first: on the alert itself when it gets new text, else on
    // the drawer's Close button.
    const drawer = inner.closest('#CartDrawer');
    if (alert.contains(document.activeElement) && drawer.classList.contains('drawer--is-open')) {
      const keep = entries.length ? alert : drawer.querySelector('.js-drawer-close');
      if (keep && keep !== document.activeElement) keep.focus();
    }
    inner.classList.toggle('drawer__inner--has-upsell-drawer-alert', entries.length > 0);
    alert.textContent = '';
    entries.forEach((entry, i) => {
      if (i) alert.append(' ');
      const at = typeof entry === 'string' || !entry.url ? -1 : String(entry.template).indexOf('[title]');
      if (at < 0) {
        alert.append(typeof entry === 'string' ? entry : logic.fill(entry.template, { title: entry.title }));
        return;
      }
      const link = document.createElement('a');
      link.href = entry.url;
      link.textContent = entry.title;
      alert.append(entry.template.slice(0, at), link, entry.template.slice(at + '[title]'.length));
    });
  }

  // Tab stays inside the open cart drawer, decided by the controls it shows NOW. The theme's trap (theme.js
  // theme.a11y.trapFocus) works from a snapshot taken when the drawer opens, before this alert and before the cart
  // lines render (buildCart is not awaited), and it has no Shift+Tab wrap. One capture-phase listener on the drawer,
  // bound once: it wraps at both ends; between them the browser moves focus. It stops every Tab inside the open
  // drawer from reaching the theme's stale html-level handler, which would otherwise wrap at its snapshot's last
  // control. Closed drawer, other keys: untouched.
  const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])';
  function keepTabInDrawer(event) {
    const drawer = event.currentTarget;
    if (event.key !== 'Tab' || !drawer.classList.contains('drawer--is-open')) return;
    event.stopPropagation();
    // Same visibility test as the theme's trap
    const controls = Array.from(drawer.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null && !el.disabled);
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    const onContainer = event.target === drawer || event.target.id === 'CartDrawerForm'; // where the theme puts focus on open
    let to = null;
    if (event.shiftKey && (event.target === first || onContainer)) to = last;
    else if (!event.shiftKey && (event.target === last || onContainer)) to = first;
    if (!to) return;
    event.preventDefault();
    to.focus();
  }

  /* ------------------------------------------------------------------ *
   * Cart drawer grids (theme setting upsell_drawer_mode = new)
   *
   * The two grids look and page exactly like the legacy ones (snippets/upsell-products.liquid); this is the
   * legacy inline script's job, redone for grids that change with the cart:
   *   grid 1 (#upsell_grid)   arrives with every cart build (snippets/upsell-drawer-feed.liquid), already
   *                           filtered for the cart. theme.js puts it into the slot and fires cart:rendered.
   *   grid 2 (#bestsell_grid) is static page markup (the page vertical's suggestions): tiles that are in the
   *                           cart or in grid 1, or that would push the delivery date, are hidden here.
   * One add at a time: one tap = ONE POST, then ONE rebuild. The cart that rebuild fetched is the one verifying
   * read: a line that is not in it is reported to the shopper (a third-party script can wipe a just-added
   * line). Nothing is ever posted again automatically, and nothing runs after that.
   * Both grids are busy (taps ignored, not queued; buttons disabled) ONLY while an add of their own runs.
   * Tile price = cart price is checked at tap time against the Prime state of the most recently RENDERED cart,
   * and every cart:rendered repaints the prices of both grids.
   * ACCEPTED RESIDUAL (orchestrator decision, PR #77): the grids do not coordinate with cart writes they did not
   * make. While such a write is in flight (between its request and the next render), a tap is checked against
   * the previous render: a tile still showing the member price can be added after DodoPrime was removed, and
   * the cart line shows the true price right after the rebuild, before checkout. The legacy drawer has no price
   * check and no repaint at all.
   * ------------------------------------------------------------------ */
  const GRID = {
    slot: '[data-upsell-drawer-slot]',
    tile: '.cross-sell-grid__item',
    add: '.cross-sell-grid__add-btn',
    more: '.cross-sell-grid__load-more-btn',
  };
  // fulfillment.vertical values as /fr renders them -> the English value the rest of the shop uses
  const VERTICALS = { beauté: 'beauty', beaute: 'beauty', frais: 'fresh', cadeaux: 'gifting', bébé: 'babybest', bebe: 'babybest' };
  const grids = {
    adding: null, // the ONE add in flight (grids busy): { id, variantId, productId, title, price, properties, source, phase, key, line }
    renderTimer: null, // set while the rebuild after our add is awaited
    pages: {}, // grid section -> pages shown ("Load more"); back to 1 with every cart build, like the legacy grid
    // The shopper's latest grid control ({ el, section, productId, role, index }): the tapped "+ Add" at a tap,
    // then whatever grid control focus moves to (any other focus clears it). keepGridFocus, the ONE focus owner,
    // puts focus back when a cart build or the paging takes that control away
    lastFocus: null,
    cartJSON: null, // the cart of the last rendered cart build (cart.js shape, JSON text), from cart:rendered
    // Product ids posted with a 200 or no answer and no rendered cart since: they may be in the cart already, so
    // they are out of BOTH grids (never posted twice) until the next rendered cart decides
    held: {},
    syncOwed: false, // an add of ours was not confirmed in time: the next rendered cart is published as cart:updated
    // Those adds (entries), and unanswered ones a rendered cart did not show yet: rendered carts are watched for
    // their line (WATCH_RENDERS at most), each says what happened
    unconfirmed: [],
    // Product ids Shopify refused (422, e.g. sold out): out of both grids until the page is rendered again, so
    // another tap cannot repeat the error
    refused: {},
  };

  function drawerEl(selector) {
    return document.querySelector('#CartDrawer ' + selector);
  }

  function gridStrings() {
    const slot = drawerEl(GRID.slot);
    const strings = {};
    Object.keys((slot && slot.dataset) || {}).forEach((key) => {
      const match = /^t([A-Z])(.*)$/.exec(key);
      if (match) strings[match[1].toLowerCase() + match[2]] = slot.dataset[key];
    });
    return strings;
  }

  function gridList() {
    const slot = drawerEl(GRID.slot);
    if (!slot) return [];
    return [slot.querySelector('#upsell_grid'), drawerEl('#bestsell_grid')].filter(Boolean);
  }

  function visibleAddButtons(grid) {
    return Array.from(grid.querySelectorAll(GRID.tile))
      .filter((tile) => !tile.classList.contains('is-hidden') && !tile.classList.contains('is-pending'))
      .map((tile) => tile.querySelector(GRID.add))
      .filter(Boolean);
  }

  function tilePrices(tile) {
    return {
      price: parseInt(tile.dataset.price, 10) || 0,
      comparePrice: parseInt(tile.dataset.comparePrice, 10) || 0,
      primeDiscount: parseInt(tile.dataset.primeDiscount, 10) || 0,
    };
  }

  // Same DOM as the legacy updateGridPrices. The painted price is kept on the tile: a tap compares it with
  // what the cart would charge.
  function paintTilePrice(tile, primeCart) {
    const row = tile.querySelector('[data-price-row]');
    if (!row) return;
    const shown = logic.gridPrice(tilePrices(tile), primeCart);
    tile.dataset.shownPrice = shown.price;
    row.textContent = '';
    const span = (className, cents) => {
      const el = document.createElement('span');
      el.className = className;
      el.textContent = money(cents);
      row.appendChild(el);
    };
    if (shown.compare) {
      span('cross-sell-grid__price cross-sell-grid__price--sale', shown.price);
      span('cross-sell-grid__price cross-sell-grid__price--compare', shown.compare);
    } else {
      span('cross-sell-grid__price', shown.price);
    }
  }

  // Which tiles show, "Load more", Prime prices, empty grid = no heading. Runs after every cart build.
  function paintGrids() {
    const slot = drawerEl(GRID.slot);
    const feed = slot && slot.querySelector('[data-upsell-feed]');
    if (!feed) return; // not mode new, or the first cart build is not in yet: tiles stay as rendered
    const primeCart = feed.dataset.primeCart === 'true';
    const exclude = Object.assign({}, grids.held, grids.refused);
    String(feed.dataset.cartProducts || '')
      .split('|')
      .filter(Boolean)
      .forEach((id) => {
        exclude[id] = true;
      });
    const cartLead = parseInt(feed.dataset.cartLeadHours, 10) || 0;
    const cartSameDay = feed.dataset.cartSameDay === 'true';
    const cartRental = feed.dataset.cartRental === 'true';

    gridList().forEach((grid) => {
      const section = grid.dataset.section;
      const first = section === 'upsell';
      const tiles = Array.from(grid.querySelectorAll(GRID.tile));
      const data = tiles.map((tile) => ({
        productId: tile.dataset.productId,
        leadHours: parseInt(tile.dataset.leadHours, 10) || 0,
        nextDayOnly: tile.dataset.nextDayOnly === 'true',
        rental: tile.dataset.rental === 'true',
      }));
      // Grid 1 was filtered against this very cart by Liquid; grid 2 is page markup and is filtered here
      const eligible = first
        ? data.map((tile) => !grids.held[tile.productId] && !grids.refused[tile.productId])
        : logic.gridEligible(data, { exclude, cartLead, cartSameDay, cartRental });
      const perPage = parseInt(grid.dataset.perPage, 10) || 6;
      const page = logic.gridPage(eligible, perPage * (grids.pages[section] || 1));

      tiles.forEach((tile, i) => {
        tile.classList.remove('is-pending');
        tile.classList.toggle('is-hidden', !page.visible[i]);
        paintTilePrice(tile, primeCart);
      });
      const row = grid.closest('.cross-sell-section');
      const more = row && row.querySelector(GRID.more);
      if (more) more.classList.toggle('is-hidden', !page.more);
      // Every tile hidden: the heading goes too
      if (row) row.hidden = !eligible.some(Boolean);

      // A product shown in grid 1 is not repeated in grid 2
      if (first) {
        data.forEach((tile) => {
          exclude[tile.productId] = true;
        });
      }
    });
  }

  // The legacy button states: "Adding..." (dimmed), tick, back to "+ Add"
  function gridIcon(type) {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    const path = document.createElementNS(ns, 'path');
    svg.setAttribute('viewBox', type === 'check' ? '0 0 24 24' : '0 0 16 16');
    path.setAttribute('d', type === 'check' ? 'M5 12l5 5L20 7' : 'M8 3v10M3 8h10');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    return svg;
  }

  function paintButton(button, state, strings) {
    const text = button.querySelector('.btn-text');
    const icon = button.querySelector('.btn-icon');
    button.classList.toggle('adding', state === 'adding');
    button.classList.toggle('success', state === 'success');
    button.disabled = state !== 'default';
    if (text) text.textContent = state === 'adding' ? strings.adding || '' : state === 'success' ? '' : strings.add || '';
    if (icon && state !== 'adding') {
      icon.textContent = '';
      icon.appendChild(gridIcon(state === 'success' ? 'check' : 'plus'));
    }
  }

  // Busy while our own add runs, on top of the markup (grid 1 is replaced by every cart build)
  function paintBusy() {
    const strings = gridStrings();
    const entry = grids.adding;
    gridList().forEach((grid) => {
      if (entry) grid.setAttribute('aria-busy', 'true');
      else grid.removeAttribute('aria-busy');
      const row = grid.closest('.cross-sell-section');
      const more = row && row.querySelector(GRID.more);
      if (more) more.disabled = !!entry;
      grid.querySelectorAll(GRID.add).forEach((button) => {
        // Own pill: "Adding...", the tick once answered, back to "+ Add" as soon as Shopify refused it (the alert
        // says why); every other button only waits, disabled, until this add is over
        const own = entry && entry.variantId === button.dataset.variantId;
        if (own) paintButton(button, { added: 'success', rejected: 'default' }[entry.phase] || 'adding', strings);
        if (!own || entry.phase === 'rejected') button.disabled = !!entry;
      });
    });
  }

  // After a verified add: tell the rest of the page, the way the legacy grid did ({ detail: { cart } }, cart.js
  // shape). Listeners: Fresh collection cards and Fresh PDP (quantity controls), smart-cart, pre-checkout, the
  // PDP add-on block. The cart is the one the drawer rebuild rendered: no extra request. It starts no rebuild.
  function publishCart() {
    let cart = null;
    try {
      cart = JSON.parse(grids.cartJSON);
    } catch (e) {
      cart = null;
    }
    if (!cart || !Array.isArray(cart.items)) return;
    publishing = true;
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } finally {
      publishing = false;
    }
  }

  function onGridAdd(button) {
    const tile = button.closest(GRID.tile);
    const grid = button.closest('.cross-sell-grid');
    const variantId = button.dataset.variantId;
    const slot = drawerEl(GRID.slot);
    const feed = slot && slot.querySelector('[data-upsell-feed]');
    if (!tile || !grid || !variantId || grids.held[tile.dataset.productId]) return;

    // Card price = cart price: the tile shows one price, the cart charges by the Prime state of the most
    // recently rendered cart. When that would be more than shown, nothing is posted.
    const primeCart = !!feed && feed.dataset.primeCart === 'true';
    const verdict = logic.gridTap({
      inert: !!grids.adding || !feed,
      shown: Number(tile.dataset.shownPrice),
      charge: logic.gridPrice(tilePrices(tile), primeCart).price,
    });
    if (verdict === 'ignore') return;
    drawerAnnounce(''); // a new operation: the last one's outcome is not current any more
    const titleEl = tile.querySelector('.cross-sell-grid__title');
    const title = titleEl ? titleEl.textContent.trim() : '';
    // Grid 1 was resolved for the cart: placement `fresh_gap` for a DodoFresh-only cart below the minimum, else
    // `drawer`. Grid 2 (page suggestions) is always `drawer`.
    const section = grid.dataset.section;
    const placement = section === 'upsell' && feed.dataset.placement === 'fresh_gap' ? 'fresh_gap' : 'drawer';
    if (verdict === 'price') {
      paintTilePrice(tile, primeCart);
      setAlert('tile:' + tile.dataset.productId, [logic.fill(gridStrings().priceChanged, { title, price: money(Number(tile.dataset.shownPrice)) })]);
      return;
    }

    // Today's line properties first (_vertical, Preorder), then where the add came from.
    // The tile prints the first vertical value as stored; on /fr that can be the accented French one.
    const vertical = button.dataset.vertical || 'gifting';
    const properties = { _vertical: VERTICALS[vertical] || vertical };
    const prepHours = parseInt(button.dataset.prepTimeHours, 10);
    if (prepHours > 0) properties.Preorder = prepHours + ' hours';
    properties._upsell_placement = placement;
    properties._upsell_rule = tile.dataset.upsellRule || 'collection';

    const entry = {
      id: Number(variantId),
      variantId,
      productId: tile.dataset.productId,
      title,
      price: parseInt(tile.dataset.price, 10) || 0,
      properties,
      source: section !== 'upsell' ? 'drawer_bestsellers' : placement === 'fresh_gap' ? 'upsell_fresh_gap' : 'upsell_drawer',
      phase: 'adding',
      key: null,
      line: null,
      tracked: false, // analytics sent (trackTileAdd): never twice for one add
    };
    grids.adding = entry;
    // An earlier unanswered add of this tile is no longer watched: a line of it in the cart is credited to this add
    // only, so one line is never tracked twice (if both writes land, the cart shows quantity 2)
    grids.unconfirmed = grids.unconfirmed.filter((watched) => watched.productId !== entry.productId);
    grids.lastFocus = { el: button, section, productId: tile.dataset.productId, role: 'add', index: visibleAddButtons(grid).indexOf(button) };
    // This tile's own last outcome (price changed, not added) asked for exactly this tap; every other entry stays
    setAlert(alertId(entry), []);
    paintBusy();

    logic
      .drawerWrite({ id: entry.id, properties }, io)
      .then(
        (result) => result,
        () => ({ outcome: 'uncertain' })
      )
      .then((result) => {
        if (result.outcome === 'added') {
          entry.phase = 'added';
          entry.line = result.line;
          entry.key = result.line.key || null;
        } else if (result.outcome === 'rejected') {
          // Definite: nothing was written. Say why now (Shopify's message); the tile leaves both grids, so another
          // tap cannot repeat the error (grid 2 is page markup and would keep offering it).
          entry.phase = 'rejected';
          grids.refused[entry.productId] = true;
          setAlert(alertId(entry), [result.message || logic.fill(gridStrings().failedSoldOut, { title })]);
        } else {
          entry.phase = 'uncertain';
        }
        // A 200 or no answer: the product may be in the cart already. Out of both grids at once, until the
        // next rendered cart decides (it stays out as in-cart, or comes back).
        if (entry.phase !== 'rejected') grids.held[entry.productId] = true;
        paintGrids();
        paintBusy();
        // ONE rebuild. CartForm listens to cart:build (theme.js), smart-cart too.
        clearTimeout(grids.renderTimer);
        grids.renderTimer = setTimeout(() => finishGrids(false), EXTRAS_DEADLINE_MS);
        document.dispatchEvent(new CustomEvent('cart:build'));
      });
  }

  // Line keys and formatted total of the cart the drawer rendered last
  function renderedCart() {
    const products = drawerEl('[data-products]');
    if (!products) return null;
    const items = products.querySelector('.cart__items');
    return {
      keys: Array.from(products.querySelectorAll('.cart__item[data-key]')).map((line) => line.dataset.key),
      total: items && items.dataset.cartSubtotal ? money(Number(items.dataset.cartSubtotal)) : '',
    };
  }

  // Analytics for a tile add the rendered cart shows (atc_source, and the line's _upsell_rule / _upsell_placement).
  // Once per entry: an entry is confirmed either at its own finish or by a late render, and the flag makes a
  // second count impossible either way.
  function trackTileAdd(entry) {
    if (entry.tracked) return;
    entry.tracked = true;
    document.dispatchEvent(
      new CustomEvent('ajaxProduct:tracked', {
        detail: {
          product: entry.line || {
            variant_id: entry.id,
            product_id: Number(entry.productId),
            title: entry.title,
            price: entry.price,
            quantity: 1,
            properties: entry.properties,
          },
          quantityAdded: 1,
          atcSource: entry.source,
        },
      })
    );
  }

  // The alert entry of a tile: its last outcome. A new add of the same tile is the retry that outcome asked for.
  function alertId(entry) {
    return 'tile:' + entry.productId;
  }

  // Rendered carts an unconfirmed add is watched for: an unanswered write can still land on the server after the
  // deadline and after the verifying read. Then it is dropped (alert gone, nobody watches: never tracked late).
  const WATCH_RENDERS = 3;

  // An add no rendered cart has confirmed: "We couldn't confirm ... check your cart below", watched
  function watchAdd(entry, strings) {
    entry.watch = WATCH_RENDERS;
    grids.unconfirmed.push(entry);
    setAlert(alertId(entry), [logic.fill(strings.tileUnconfirmed, { title: entry.title })], { open: true });
  }

  // Each rendered cart says what happened to the watched adds, each in its own entry:
  //   line there          - "Added", tracked now (once: trackTileAdd), the page hears the cart
  //   line absent, 200    - the server answered and the line is gone: "wasn't added, tap + Add" (definite)
  //   line absent, no answer - it may still land: still unconfirmed, until WATCH_RENDERS rendered carts lacked it
  function lateVerdicts(cart, strings) {
    grids.unconfirmed = grids.unconfirmed.filter((entry) => {
      if (logic.lineLanded(cart.keys, entry)) {
        trackTileAdd(entry);
        grids.syncOwed = true;
        setAlert(alertId(entry), [logic.fill(strings.statusAdded, { title: entry.title })], { render: renderSeq });
        return false;
      }
      if (entry.phase === 'added') {
        setAlert(alertId(entry), [logic.fill(strings.tileNotAdded, { title: entry.title })]);
        return false;
      }
      entry.watch -= 1;
      if (entry.watch > 0) return true;
      setAlert(alertId(entry), []);
      return false;
    });
  }

  // The drawer live region: only the current verified outcome (cleared by every new operation, cart render and
  // drawer open)
  function drawerAnnounce(text) {
    const status = drawerEl('[data-upsell-drawer-status]');
    if (status) status.textContent = text;
  }

  // The verifying read is the cart the drawer rebuild fetched. Nothing is ever posted again:
  //   verified         - the line is in that cart: tracked, announced
  //   verified missing - the server answered 200 and that cart does not have the line: the shopper is told to tap
  //                      "+ Add" again
  //   unconfirmed      - no answer (network error, abort, deadline) and the line is not there, or the rebuild did
  //                      not arrive before the deadline (rendered = false): the shopper is told to check the cart,
  //                      later rendered carts are watched (lateVerdicts). The tile stays tappable (owner rule).
  function finishGrids(rendered) {
    clearTimeout(grids.renderTimer);
    grids.renderTimer = null;
    const entry = grids.adding;
    grids.adding = null;
    const strings = gridStrings();
    const cart = rendered ? renderedCart() : null;
    if (cart) lateVerdicts(cart, strings);

    if (entry) {
      // The pill goes back to "+ Add" (the tile itself leaves the grid when the product is in the cart)
      gridList().forEach((grid) => {
        grid.querySelectorAll(GRID.add + '[data-variant-id="' + entry.variantId + '"]').forEach((button) => {
          paintButton(button, 'default', strings);
        });
      });
      if (entry.phase === 'rejected') {
        // Already told (Shopify's own message, its entry stays)
      } else if (!cart) {
        watchAdd(entry, strings);
        // The write may well be in the cart: the page still has to hear about it, from the next rendered cart
        grids.syncOwed = true;
      } else if (logic.lineLanded(cart.keys, entry)) {
        trackTileAdd(entry);
        grids.syncOwed = true; // published below, once
        drawerAnnounce(logic.drawerStatus([entry.title], cart.total, strings));
      } else if (entry.phase === 'added') {
        setAlert(alertId(entry), [logic.fill(strings.tileNotAdded, { title: entry.title })]);
      } else {
        watchAdd(entry, strings);
      }
    }

    // ONE cart:updated per rendered cart that owes one
    if (rendered) payCartSync();
    paintBusy();
    if (!rendered) keepGridFocus(); // a render recovers focus once, after everything (onCartRendered)
  }

  function payCartSync() {
    if (!grids.syncOwed || !grids.cartJSON) return;
    grids.syncOwed = false;
    publishCart();
  }

  function onCartRendered(event) {
    // The cart page form rebuilds through the same theme.js code, without a location
    if (!event.detail || event.detail.location !== 'cart-drawer') return;
    holdAlerts(() => afterDrawerRender(event));
    keepGridFocus();
  }

  // Every focusable control of a tile, by role (cross-sell-grid-item.liquid: image link, "+ Add", title link)
  const TILE_CONTROLS = { image: '.cross-sell-grid__image a', add: GRID.add, title: '.cross-sell-grid__title' };

  function noteGridFocus(event) {
    const el = event.target;
    const row = drawerEl(GRID.slot) && el.closest ? el.closest('.cross-sell-section') : null;
    grids.lastFocus = null;
    if (!row || !el.closest('#CartDrawer')) return;
    const list = row.querySelector('.cross-sell-grid');
    if (!list) return;
    const pills = visibleAddButtons(list);
    if (el.matches(GRID.more)) {
      grids.lastFocus = { el, section: list.dataset.section, role: 'more', index: pills.length };
      return;
    }
    const tile = el.closest(GRID.tile);
    const role = tile && Object.keys(TILE_CONTROLS).find((key) => el.matches(TILE_CONTROLS[key]));
    if (!role) return;
    grids.lastFocus = { el, section: list.dataset.section, productId: tile.dataset.productId, role, index: pills.indexOf(tile.querySelector(GRID.add)) };
  }

  // The first usable title link of these tiles (a link: activating it navigates, it never adds)
  function firstTitle(tiles) {
    for (let i = 0; i < tiles.length; i += 1) {
      const link = tiles[i] && tiles[i].querySelector(TILE_CONTROLS.title);
      if (link && gridControlShown(link)) return link;
    }
    return null;
  }

  // Is this grid control shown? Not when it left the page (a cart build replaced grid 1), when the grid's paging
  // hides its tile or the "Load more" button, or when anything above it is display: none (what makes the browser's
  // offsetParent null; computed here so it holds without layout too)
  function gridControlShown(el) {
    if (!el.isConnected || el.closest(GRID.tile + '.is-hidden, ' + GRID.tile + '.is-pending')) return false;
    if (el.matches(GRID.more) && el.classList.contains('is-hidden')) return false;
    for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
      if (node.hidden || window.getComputedStyle(node).display === 'none') return false;
    }
    return true;
  }

  // The ONE focus owner, for the shopper's latest grid control when it no longer has focus: it is no longer shown
  // (a tapped tile went into the cart, a cart build replaced grid 1, a paging reset or "Load more" hid it, the empty
  // cart hides the list), or focus fell to <body> (Chromium blurs a focused control when it gets disabled, e.g. a
  // busy "+ Add"). Then: that control when it is shown and enabled, else the same control of the same product in
  // the new grid; else (never another product's "+ Add": Enter there would add it) the title link of the tile now
  // in its place, else the first title link shown, else the drawer's Close button. Every candidate must be shown
  // and enabled. Runs once per cart render, after an add's
  // deadline and after "Load more", never while an add of ours runs. Only while the drawer is open: closing it
  // (Escape, Close, overlay) blurs focus on purpose, and the saved control is dropped then. Focus the shopper put
  // on another control stays.
  function keepGridFocus() {
    const last = grids.lastFocus;
    if (!last || grids.adding) return;
    const active = document.activeElement;
    if (active === last.el && gridControlShown(last.el)) return;
    if (active && active !== last.el && active !== document.body && active.isConnected) return;
    const drawer = document.getElementById('CartDrawer');
    if (!drawer || !drawer.classList.contains('drawer--is-open')) {
      grids.lastFocus = null;
      return;
    }
    grids.lastFocus = null;
    const grid = gridList().find((candidate) => candidate.dataset.section === last.section);
    const usable = (el) => !!el && !el.disabled && gridControlShown(el);
    let target = usable(last.el) ? last.el : null;
    if (!target && grid) {
      const row = grid.closest('.cross-sell-section');
      const more = row && row.querySelector(GRID.more);
      const tiles = Array.from(grid.querySelectorAll(GRID.tile)).filter(gridControlShown);
      if (last.role === 'more') {
        target = usable(more) ? more : null;
      } else {
        // The same product: its own control, its "+ Add" included
        const tile = tiles.find((candidate) => candidate.dataset.productId === last.productId);
        const same = tile && tile.querySelector(TILE_CONTROLS[last.role]);
        target = usable(same) ? same : null;
      }
      target = target || firstTitle(tiles.slice(Math.max(last.index, 0), Math.max(last.index, 0) + 1)) || firstTitle(tiles);
    }
    if (!target) {
      const close = drawerEl('.js-drawer-close');
      target = usable(close) ? close : null;
    }
    if (target) target.focus();
  }

  function afterDrawerRender(event) {
    drawerAnnounce(''); // the add this render verifies (finishGrids) announces itself again
    renderSeq += 1;
    dropSettledAlerts((alert) => alert.render !== undefined && alert.render < renderSeq);
    syncBlocksFromRender(event.detail.cartJSON);
    grids.pages = {};
    grids.cartJSON = event.detail.cartJSON || null;
    // This cart was built after every answered or abandoned POST of ours (unless one is out right now): it decides
    if (!grids.adding || grids.renderTimer) grids.held = {};
    paintGrids();
    if (grids.renderTimer) {
      finishGrids(true); // the rebuild we asked for after our own add
      return;
    }
    paintBusy(); // our POST may still be out while grid 1 was replaced
    if (grids.adding) return; // its own rebuild settles everything
    // A rebuild that came after our deadline: the page did not hear it yet, the shopper hears what happened
    const cart = renderedCart();
    if (cart) lateVerdicts(cart, gridStrings());
    payCartSync();
  }

  /* ------------------------------------------------------------------ *
   * Dedupe with "You may also like": a product in the add-on pool never shows again in recommendations
   * ------------------------------------------------------------------ */
  function dedupeRecommendations(section) {
    const pool = {};
    allBlocks().forEach((block) => {
      block.querySelectorAll(SEL.option).forEach((option) => {
        pool[option.dataset.productId] = true;
      });
    });
    if (!Object.keys(pool).length) return;

    const sections = section ? [section] : Array.from(document.querySelectorAll('product-recommendations'));
    sections.forEach((el) => {
      // sections/product-recommendations.liquid renders 2 spare cards beyond data-limit, hidden by CSS. A pool
      // card is REMOVED (the uniform grid clears rows by position) only while a spare can take its place, so
      // the row stays full; with no spare left the row is kept as it is.
      const cards = Array.from(el.querySelectorAll('.grid-product[data-product-id]'));
      let spare = cards.length - (parseInt(el.dataset.limit, 10) || cards.length);
      cards.forEach((card) => {
        if (spare > 0 && pool[card.dataset.productId]) {
          card.remove();
          spare -= 1;
        }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Staff debug overlay: ?upsell_debug=1 (kept for the session), ?upsell_debug=0 clears. English only.
   * ------------------------------------------------------------------ */
  function debugEnabled() {
    const match = /[?&]upsell_debug=([01])/.exec(window.location.search);
    try {
      if (match && match[1] === '1') window.sessionStorage.setItem('ddmUpsellDebug', '1');
      if (match && match[1] === '0') window.sessionStorage.removeItem('ddmUpsellDebug');
      return window.sessionStorage.getItem('ddmUpsellDebug') === '1';
    } catch (e) {
      return !!(match && match[1] === '1');
    }
  }

  const DEBUG_CSS =
    '.upsell-debug{position:fixed;left:8px;bottom:8px;z-index:var(--z-toast,1100);box-sizing:border-box;' +
    'max-width:min(480px,calc(100vw - 16px));max-height:50vh;overflow:auto;padding:8px 10px;' +
    'background:#111;color:#f5f5f5;font:12px/1.4 ui-monospace,Menlo,Consolas,monospace;border-radius:6px}' +
    // The theme paints every table #e6e6e6: reset table and rows too, or the light text is unreadable
    '.upsell-debug table,.upsell-debug tbody,.upsell-debug tr{background:none;color:inherit}' +
    '.upsell-debug table{width:100%;margin:4px 0 10px;border-collapse:collapse}' +
    '.upsell-debug th,.upsell-debug td{padding:1px 8px 1px 0;border:0;text-align:left;vertical-align:top;' +
    'font:inherit;color:inherit;background:none}' +
    // Never on top of an open drawer (it covered half of the cart on mobile)
    '.js-drawer-open .upsell-debug{display:none}' +
    '.upsell-debug__close{float:right;min-width:44px;min-height:44px;margin:-8px -10px 0 8px;border:0;' +
    'background:none;color:inherit;font:inherit;cursor:pointer}' +
    '.upsell-debug__warn{color:#ffb4a8}';

  function debugRows(data) {
    // Pool items get their live state: shown, pool (waiting to refill the row) or in_cart
    const live = {};
    allBlocks().forEach((block) => {
      const state = stateFor(block);
      if (!state) return;
      state.config.items.forEach((item, i) => {
        const option = state.options[i];
        if (state.inCart[item.p]) live[item.p] = 'in_cart';
        else live[item.p] = option && !option.hidden ? 'shown' : 'pool';
      });
    });
    return (data.candidates || []).map((candidate) => ({
      handle: candidate.h,
      rule: candidate.r,
      code: candidate.c === 'pool' && live[candidate.p] ? live[candidate.p] : candidate.c,
    }));
  }

  let debugClosed = false;

  function renderDebug() {
    if (debugClosed || !debugEnabled()) return;
    let panel = document.querySelector('.upsell-debug');
    if (!panel) {
      const style = document.createElement('style');
      style.textContent = DEBUG_CSS;
      document.head.appendChild(style);
      panel = document.createElement('aside');
      panel.className = 'upsell-debug';
      panel.setAttribute('aria-label', 'Upsell debug');
      document.body.appendChild(panel);
    }
    panel.textContent = '';

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'upsell-debug__close';
    close.textContent = 'Close';
    close.addEventListener('click', () => {
      debugClosed = true; // stays closed until the next page load
      panel.remove();
    });
    panel.appendChild(close);

    const sources = Array.from(document.querySelectorAll(SEL.debug)).filter((el) => !inModal(el));
    if (!sources.length) {
      panel.appendChild(
        document.createTextNode('upsell debug: nothing to show (no upsell surface here, or "Preview upsell rules" is off on this theme)')
      );
      return;
    }
    sources.forEach((source) => {
      const data = parseJSON(source);
      if (!data) return;
      const head = document.createElement('div');
      head.textContent =
        data.placement + ' · ' + data.anchor + ' · ' + data.vertical +
        ' · rules matched: ' + ((data.rulesMatched || []).join(', ') || 'none') +
        // Cart drawer grid 1: where the fill comes from, and the DodoFresh gap window
        (data.fill ? ' · fill: ' + data.fill : '') +
        (data.window ? ' · first: ' + money(data.window[0]) + ' - ' + money(data.window[1]) : '');
      panel.appendChild(head);
      if (data.rulesActive > data.rulesLooped) {
        const warn = document.createElement('div');
        warn.className = 'upsell-debug__warn';
        warn.textContent =
          'WARNING: ' + data.rulesActive + ' Active rules but Liquid looped ' + data.rulesLooped + ' (limit 50)';
        panel.appendChild(warn);
      }
      const table = document.createElement('table');
      const header = table.insertRow();
      ['candidate', 'rule', 'code'].forEach((label) => {
        const th = document.createElement('th');
        th.textContent = label;
        header.appendChild(th);
      });
      debugRows(data).forEach((row) => {
        const tr = table.insertRow();
        [row.handle, row.rule, row.code].forEach((value) => {
          tr.insertCell().textContent = value;
        });
      });
      panel.appendChild(table);
    });
  }

  /* ------------------------------------------------------------------ *
   * Events (all delegated)
   * ------------------------------------------------------------------ */
  function blockOf(el) {
    const block = el && el.closest ? el.closest(SEL.block) : null;
    return block ? stateFor(block) : null;
  }

  function onInput(event) {
    const target = event.target;
    if (!target || !target.matches) return;

    if (target.matches(SEL.checkbox)) {
      // A checkbox fires input and change: act once, or the live region announces twice
      if (event.type !== 'change') return;
      const state = blockOf(target);
      if (!state) return;
      if (state.busy) {
        // The extras are being added right now: this tick cannot be part of it, so it is refused, not dropped later
        target.checked = !target.checked;
        return;
      }
      state.notice = '';
      const sums = refresh(state);
      const option = target.closest(SEL.option);
      const item = state.config.items[Number(option.dataset.index)] || {};
      const i18n = state.i18n;
      announce(
        state,
        logic.fill(target.checked ? i18n.statusAdded : i18n.statusRemoved, {
          title: item.title || '',
          total: money(sums.total),
        })
      );
      renderDebug();
      return;
    }

    // Variant picked in a card's field: never ticks; image, price, totals, live region and the added variant follow
    if (target.matches(SEL.variant)) {
      // A select fires input and change: act once
      if (event.type !== 'change') return;
      const state = blockOf(target);
      const option = target.closest(SEL.option);
      const item = state && option ? state.config.items[Number(option.dataset.index)] : null;
      if (!item) return;
      if (state.busy) {
        // The add in progress uses the snapshot taken at the press: refused, like a tick
        target.value = String(item.v);
        return;
      }
      const variant = logic.chooseVariant(item, target.value);
      if (!variant) return;
      option.dataset.variantId = String(variant.v);
      const img = option.querySelector('[data-upsell-img]');
      if (img && variant.img) img.src = variant.img;
      paintPrice(state, option, item, state.paintedPrime);
      syncSoldOut(state, option, item, target.disabled); // a stocked variant frees a card blocked by a sold-out one
      state.notice = '';
      const sums = refresh(state);
      const checkbox = checkboxOf(option);
      const ticked = !!(checkbox && checkbox.checked && !checkbox.disabled);
      // Ticked: the shopper hears what is now added and the new total. Unticked: nothing changed in the order, and
      // an earlier announcement about this card would be stale
      announce(state, ticked ? logic.fill(state.i18n.statusAdded, { title: item.title, total: money(sums.total) }) : '');
      renderDebug();
      return;
    }

    // PDP Prime opt-in toggled: the cart will (or will not) charge member prices, cards and totals follow
    if (target.matches('[data-prime-upsell-cb]')) {
      if (event.type !== 'change') return;
      allBlocks().forEach((block) => {
        const state = stateFor(block);
        if (state) refresh(state);
      });
      renderDebug();
      return;
    }

    // Quantity typed, or a personalisation / option field filled: totals and the waiting copy follow
    if (target.form && (target.name === 'quantity' || String(target.name).indexOf('properties[') === 0)) {
      const state = stateForForm(target.form);
      if (state) refresh(state);
    }
  }

  function onClick(event) {
    const target = event.target;
    if (!target || !target.closest) return;

    // Cart drawer grids, mode `new` only (no slot = legacy grids, handled by their own inline script)
    const gridButton = target.closest(GRID.add + ', ' + GRID.more);
    if (gridButton && gridButton.closest('#CartDrawer') && drawerEl(GRID.slot)) {
      if (gridButton.matches(GRID.more)) {
        const section = gridButton.dataset.section;
        const grid = gridList().find((candidate) => candidate.dataset.section === section);
        const before = grid ? visibleAddButtons(grid) : [];
        grids.pages[section] = (grids.pages[section] || 1) + 1;
        paintGrids();
        // The last page: the focused button hides itself. The keyboard user goes on with the first tile it revealed
        // (its image link, never an "+ Add"), else the first title link shown, else Close.
        if (grid && document.activeElement === gridButton && !gridControlShown(gridButton)) {
          const pills = visibleAddButtons(grid);
          const revealed = pills.find((pill) => before.indexOf(pill) === -1);
          const tile = revealed && revealed.closest(GRID.tile);
          const tiles = Array.from(grid.querySelectorAll(GRID.tile)).filter(gridControlShown);
          const target = (tile && tile.querySelector('a[href]')) || firstTitle(tiles) || drawerEl('.js-drawer-close');
          if (target) target.focus();
        }
        keepGridFocus();
      } else {
        event.preventDefault();
        onGridAdd(gridButton);
      }
      return;
    }

    // Quantity +/- buttons change the input without a change event
    const stepper = target.closest('.js-qty__adjust');
    if (stepper) {
      const wrapper = stepper.closest('.js-qty__wrapper');
      const input = wrapper && wrapper.querySelector('input[name="quantity"]');
      const state = input && input.form ? stateForForm(input.form) : null;
      if (state) refresh(state);
      return;
    }

    // Add pressed with extras ticked while a required field is empty: the theme blocks the add and shows
    // its error; the summary explains that the extras wait
    const button = target.closest('[data-add-to-cart], [data-sticky-atc-btn]');
    if (button) {
      const form = button.dataset.formId ? document.getElementById(button.dataset.formId) : button.closest('form');
      const state = stateForForm(form);
      if (state) {
        state.waiting = missingInput(form);
        refresh(state);
      }
    }
  }

  function onVariantChange(event) {
    const section = event.target;
    const block = section && section.querySelector ? section.querySelector(SEL.block) : null;
    const state = block ? stateFor(block) : null;
    if (!state) return;
    const variant = event.detail && event.detail.variant;
    // The offer itself does not depend on the variant: only the running total and its visibility follow
    state.available = !!(variant && variant.available);
    if (variant) state.variantPrice = variant.price;
    refresh(state);
  }

  function init() {
    allBlocks().forEach((block) => {
      const state = stateFor(block);
      if (state) refresh(state);
    });
    dedupeRecommendations();
    paintGrids();
    renderDebug();
  }

  document.addEventListener('submit', onSubmit);
  document.addEventListener('ajaxProduct:error', onMainError);
  document.addEventListener('change', onInput, true);
  document.addEventListener('input', onInput, true);
  document.addEventListener('click', onClick);
  // variantChange is dispatched on the product section and does not bubble: listen in the capture phase
  document.addEventListener('variantChange', onVariantChange, true);
  document.addEventListener('recommendations:loaded', (event) => {
    dedupeRecommendations(event.detail && event.detail.section);
  });
  document.addEventListener('ajaxProduct:added', (event) => {
    const upsell = event.detail && event.detail.upsell;
    // A PDP add's result is one entry; it supersedes the previous PDP add's (one render). Tile entries stay.
    holdAlerts(() => {
      dropSettledAlerts((alert) => alert.id.indexOf('pdp:') === 0);
      const id = 'pdp:' + (alertSeq += 1);
      if (upsell) upsell.alertId = id; // its add's drawer build may still revise it (reviseFromRender)
      setAlert(id, (upsell && upsell.alert) || []);
    });
  });
  const cartDrawer = document.getElementById('CartDrawer');
  if (cartDrawer && !cartDrawer.upsellTabTrap) {
    cartDrawer.upsellTabTrap = true;
    cartDrawer.addEventListener('keydown', keepTabInDrawer, true);
  }
  document.addEventListener('drawerOpen', () => {
    // Every settled entry from before this open goes; an unresolved tile add stays until a rendered cart decides
    dropSettledAlerts((alert) => !alert.fresh);
    drawerAnnounce('');
  });
  document.addEventListener('cart:updated', onCartEvent);
  document.addEventListener('ajaxProduct:added', onCartEvent);
  document.addEventListener('ajaxProduct:tracked', onCartEvent);
  document.addEventListener('cart:rendered', renderDebug);
  document.addEventListener('cart:rendered', onCartRendered);
  document.addEventListener('focusin', noteGridFocus);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) init();
  });

  window.DDMUpsell = { afterMainAdd, beginSubmission, endSubmission, refresh: init, deadlineMs: EXTRAS_DEADLINE_MS, _logic: logic };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})(typeof window !== 'undefined' ? window : undefined, typeof document !== 'undefined' ? document : undefined);
